import type { ReadUserPermissionsOutput } from '@directus/sdk'

type AccessType = 'read' | 'create' | 'update' | 'delete' | 'share'

type PermissionLevel = 'none' | 'partial' | 'full'

export default class PermissionsControl {
  private _data: ReadUserPermissionsOutput | undefined

  /**
   * @param {ReadUserPermissionsOutput | undefined} data - The user's permissions data object returned from the Directus Read User Permissions endpoint.
   * @description
   * This constructor takes in the user's permissions data object and stores it in this._data.
   * The permissions data object should have the following format.
   * {
   *   "collections": {
   *     "collectionName1": {
   *       "read": {
   *         "access": string,
   *         "fields": string[]
   *       },
   *       "create": {
   *         "access": string,
   *         "fields": string[]
   *       },
   *       "update": {
   *         "access": string,
   *         "fields": string[]
   *       },
   *       "delete": {
   *         "access": string,
   *         "fields": string[]
   *       },
   *       "share": {
   *         "access": string,
   *         "fields": string[]
   *       }
   *     },
   *     "collectionName2": {
   *       ... same as above
   *     }
   *   }
   * }
   */
  constructor(data?: ReadUserPermissionsOutput | undefined) {
    this._data = data
  }

  /**
   * Returns true if the user has the specified access type for the given collection key and field, false otherwise.
   * @param {string} key - The collection key to check for permissions.
   * @param {AccessType} access - The type of access to check for.
   * @param {string} field - The field to check for permissions.
   * @returns {boolean} True if the user has the specified access type for the given collection key and field, false otherwise.
   */
  public hasFieldPermission(key: string, access: AccessType, field: string): boolean {
    console.log('hasFieldPermission -- fired');
    
    const permissions = this._data?.[key]?.[access]
    console.log('hasFieldPermission -- permissions', permissions);
    if (!permissions) return false
    if (permissions.access === 'none') return false
    if (permissions.access === 'full' || permissions.fields?.[0] === '*') return true
    console.log('hasFieldPermission -- passed all ifs');

    return permissions.fields?.includes(field) ?? false
  }

  /**
   * Returns true if the user has the specified access type for the given collection key, false otherwise.
   * @param {string} key - The collection key to check for permissions.
   * @param {AccessType} access - The type of access to check for.
   * @returns {boolean} True if the user has the specified access type for the given collection key, false otherwise.
   */
  public hasAccessPermission(key: string, access: AccessType): boolean {
    const permissions = this._data?.[key]?.[access]
    if (!permissions) return false
    return permissions.access !== 'none'
  }

  /**
   * Checks if the user has a specific permission level for the given collection key and access type.
   *
   * @param {string} key - The collection key to check for permissions.
   * @param {AccessType} access - The type of access to check for.
   * @param {PermissionLevel} permissionLevel - The specific permission level to check against.
   * @returns {boolean} True if the user's access matches the specified permission level, false otherwise.
   */
  public hasSpecificPermission(
    key: string,
    access: AccessType,
    permissionLevel: PermissionLevel,
  ): boolean {
    const permissions = this._data?.[key]?.[access]
    if (!permissions) return false
    return permissions.access === permissionLevel
  }
}
